/**
 * Customer display — what a business's customers see on the till's second screen.
 *
 * The layout catalogue MIRRORS the POS (POS_V1.Web models/customer-display.ts
 * DisplayLayout, services/customer-display-content.ts DISPLAY_LAYOUT_OPTIONS, and the
 * `layout-*` palettes in components/customer-display/customer-display.component.scss).
 * Keep the keys, headings and colours identical, or this page previews a screen the
 * till does not draw.
 */

export type DisplayLayout = 'retail' | 'restaurant' | 'hotel' | 'printshop' | 'appointments' | 'farm';

/** GET BusinessManagement/{businessId}/customer-display. Read-only here. */
export interface BusinessCustomerDisplay {
  businessId: string;
  /** Null = "Match my business type". */
  layout: DisplayLayout | null;
  /** Explicit operating mode; null = derived from the business type. */
  modeKey: string | null;
  businessName: string | null;
  welcomeMessage: string | null;
  thankYouMessage: string | null;
  showSavings: boolean;
  hasFallbackImage: boolean;
  updatedAt: string | null;
}

export interface DisplayLayoutInfo {
  value: DisplayLayout;
  label: string;
  /** The sale screen's heading on the till. */
  heading: string;
  hint: string;
  palette: { navy: string; light: string; gold: string; goldLight: string; white: string; muted: string };
}

export const DISPLAY_LAYOUTS: readonly DisplayLayoutInfo[] = [
  { value: 'retail', label: 'Retail', heading: 'Order Summary',
    hint: 'Basket list with the latest item highlighted and savings per line.',
    palette: { navy: '#0d1b2a', light: '#162336', gold: '#c9a84c', goldLight: '#e2c47a', white: '#f5f0e8', muted: '#8a9ab0' } },
  { value: 'restaurant', label: 'Restaurant / café', heading: 'Your order',
    hint: 'Order view: options under each dish.',
    palette: { navy: '#1c1714', light: '#2b241f', gold: '#e8803f', goldLight: '#f2a66f', white: '#faf5ef', muted: '#c8bcaf' } },
  { value: 'hotel', label: 'Hotel front desk', heading: 'Your stay',
    hint: 'Charges read as a folio.',
    palette: { navy: '#0c1f1d', light: '#15302d', gold: '#5cc8b8', goldLight: '#8fdccf', white: '#f1f6f5', muted: '#9fb5b1' } },
  { value: 'printshop', label: 'Print shop', heading: 'Your job',
    hint: 'Job view: options under each job.',
    palette: { navy: '#17131f', light: '#241e31', gold: '#b79cf2', goldLight: '#d2c1f7', white: '#f6f5f8', muted: '#a9a1b8' } },
  { value: 'appointments', label: 'Appointments', heading: 'Today’s visit',
    hint: 'Visit view: services and products.',
    palette: { navy: '#1f1418', light: '#2e2126', gold: '#f39ac4', goldLight: '#f9c1db', white: '#fbf7f8', muted: '#c4adb6' } },
  { value: 'farm', label: 'Farm stand', heading: 'Your basket',
    hint: 'Weighed produce shows its weight under the name.',
    palette: { navy: '#1b2310', light: '#26311a', gold: '#b5cf6b', goldLight: '#cfe29a', white: '#f4f3ec', muted: '#a9b394' } },
];

export function layoutInfo(value: string | null | undefined): DisplayLayoutInfo {
  return DISPLAY_LAYOUTS.find(l => l.value === value) ?? DISPLAY_LAYOUTS[0];
}

function known(value: string | null | undefined): DisplayLayout | null {
  const key = (value ?? '').trim().toLowerCase();
  return DISPLAY_LAYOUTS.some(l => l.value === key) ? key as DisplayLayout : null;
}

/**
 * The layout the till actually draws, by the till's own rule: the saved layout,
 * else the effective business mode (explicit mode, else the business type's name —
 * POS resolveEffectiveBusinessMode), else retail.
 */
export function resolveDisplayLayout(
  saved: string | null | undefined,
  modeKey: string | null | undefined,
  businessTypeName: string | null | undefined,
): DisplayLayout {
  const effectiveMode = (modeKey || businessTypeName || 'retail').toLowerCase();
  return known(saved) ?? known(effectiveMode) ?? 'retail';
}
